// contexts/AdminContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import { getApiBaseURL, getCsrfHeaders } from '../utils/api';
import { safeSessionStorage } from '../utils/safeStorage';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(() => safeSessionStorage.getItem('admin_token'));
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');

  // 운영자 인증
  const handleAdminLogin = useCallback(async () => {
    // 비밀번호가 비어있는지 확인
    if (!adminPassword || adminPassword.trim() === '') {
      alert('비밀번호를 입력해주세요.');
      return;
    }

    try {
      const response = await fetch(`${getApiBaseURL()}/api/admin/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getCsrfHeaders()),
        },
        credentials: 'include',
        body: JSON.stringify({ password: adminPassword }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `서버 오류: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        safeSessionStorage.setItem('admin_token', data.admin_token);
        setAdminToken(data.admin_token);
        setShowAdminLogin(false);
        setAdminPassword('');
        const redirectPath = safeSessionStorage.getItem('admin_login_redirect_path');
        if (redirectPath) {
          safeSessionStorage.removeItem('admin_login_redirect_path');
          window.location.assign(redirectPath);
        }
      } else {
        alert(data.error || '비밀번호가 일치하지 않습니다.');
        setAdminPassword(''); // 비밀번호 초기화
      }
    } catch (error) {
      console.error('운영자 인증 오류:', error);
      alert(`운영자 인증에 실패했습니다: ${error.message}`);
      setAdminPassword(''); // 비밀번호 초기화
    }
  }, [adminPassword]);

  // 운영자 모드 종료
  const handleAdminLogout = useCallback(() => {
    safeSessionStorage.removeItem('admin_token');
    setAdminToken(null);
  }, []);

  // 운영자인지 확인
  const isAdmin = useCallback(() => {
    return adminToken !== null;
  }, [adminToken]);

  // 키보드 단축키 제거 - 보안상 단축키는 사용하지 않음

  const value = {
    adminToken,
    isAdmin,
    showAdminLogin,
    setShowAdminLogin,
    adminPassword,
    setAdminPassword,
    handleAdminLogin,
    handleAdminLogout,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

